import { CancellationToken } from "../../../../base/common/cancellation";
import { createDecorator } from "../../../../platform/instantiation/common/registry";
import { Event } from "../../../../base/common/events";
import { IDisposable } from "../../../../base/common/lifecycle";
import { StandardMouseEvent } from "../../../../base/browser/mouseEvent";
import { ServicesAccessor } from "../../../../platform/instantiation/common/instantiation";
import { VSBuffer } from "../../../../base/common/buffer";
import { URI } from "../../../../base/common/uri";
import { IProperty } from "../../../../platform/properties/common/properties";


// #region IAssetsService

export const IAssetsService = createDecorator<IAssetsService>("assetsService");
export interface IAssetsService {

    readonly _serviceBrand: undefined;

    readonly onDidChange: Event<void>;

    /** something about the assets changed OUTSIDE the service (a promote, an
     * external tool) — fires onDidChange so panels refetch */
    notifyExternalChange(): void;

    queryOptions(): Promise<AssetOptionsResult>;

    list(options: AssetFetchOptions, token?: CancellationToken, onProgress?: (progress: AssetFetchProgress) => void): Promise<AssetFetchResult>;

    /**
     * @param provider optional provider key ({@link Asset.provider}) to route DIRECTLY to the
     * owning provider — skips probing others (e.g. a cold document reopen that recorded the
     * provider). A single-provider service ignores it; the aggregate uses it. A failed hinted
     * lookup still falls back to the normal search.
     */
    loadAsset(id: string, token?: CancellationToken, provider?: string): Promise<FullAsset | null>;

    /**
     * Recovers how an asset's external resources (textures, buffers) should be fetched — the
     * owning provider's `resolveUrl` + resource base for the version identified by `ref`.
     *
     * A project document records only an asset's `src` plus its IDENTITY (provider + id); the
     * `src` alone can't say "these textures live at provider CDN urls". This lets a consumer
     * (e.g. the project-document opener) recover the resolver AT LOAD TIME from the identity,
     * so the URL-rewrite knowledge stays here in the asset service — the consumer carries none.
     * Returns null when the asset or a matching version can't be found (caller falls back to
     * plain relative resolution).
     */
    resolveResources(ref: AssetResourceRef, token?: CancellationToken): Promise<AssetResourceResolution | null>;

    registerInteractionHandler(handler: IAssetInteractionHandler): IDisposable;

    /**
     * Handles the given asset interaction by delegating to registered interaction handlers.
     * @returns a promise that resolves to true if the interaction was handled, false otherwise. If no handler was found, null is returned.
     */
    handleInteraction(interaction: AssetInteraction, token?: CancellationToken): Promise<boolean> | null;

    /**
     * Import a file into a source's LIBRARY — the "project" source copies it into the
     * active project folder (the folder IS the library; identity/kind enter the
     * DOCUMENT only when the asset gets referenced — see
     * plans/local-first-and-cloud-direction.md, import design). Returns the created
     * asset, or null when the source does not support importing (remote libraries)
     * or no project folder is active.
     */
    importAsset(request: AssetImportRequest, token?: CancellationToken): Promise<Asset | null>;

    /**
     * Remove an asset from its library — STORAGE-level only ("is it still referenced
     * by the document" is the delete COMMAND's concern, not this API's). Only assets
     * whose `capabilities.delete` is true can be deleted; returns false otherwise.
     */
    deleteAsset(asset: Asset, token?: CancellationToken): Promise<boolean>;

    /**
     * LAZY thumbnail generation for assets without one (project glbs: load once,
     * one camera fit, one render through the preview service, cached + queued by
     * the owning provider). Null = the provider cannot generate one — the UI
     * keeps its kind icon. Optional: remote catalogs ship thumbnails.
     */
    /** `options.size`: requested edge length in px — panel tiles use the
     * default (256); larger consumers (the promote card image) ask for more */
    getAssetThumbnail?(asset: Asset, token?: CancellationToken, options?: { size?: number }): Promise<string | null>;

    /** sha-256 hex of the asset's current bytes, computed LAZILY (listing
     * never hashes) — the inspector's content sha, and the same address the
     * cloud blob pool / promotions key on. Null when the provider can't
     * answer (remote assets, unreadable files). */
    getAssetContentHash?(asset: Asset, token?: CancellationToken): Promise<string | null>;

    /** the TEXT content of a text/code asset (utf-8) — what the Code panel
     * shows when the asset is selected. Null when the provider can't read it
     * (foreign provider, missing file). Callers gate by type/size — this
     * only reads and decodes. */
    getAssetText?(asset: Asset, token?: CancellationToken): Promise<string | null>;

}

export type AssetType = "texture" | "model" | "hdr" | "material" | "image" | "video" | "audio" | "code" | "text" | "document" | "archive";

// AssetType const array
export const AssetTypes: readonly AssetType[] = ["texture", "model", "hdr", "material", "image", "video", "audio", "code", "text", "document", "archive"];

const EXTENSION_TYPES: Record<string, AssetType> = {
    glb: "model", gltf: "model", fbx: "model", obj: "model", usdz: "model", vrm: "model",
    hdr: "hdr", exr: "hdr",
    png: "image", jpg: "image", jpeg: "image", webp: "image", avif: "image", gif: "image", svg: "image",
    ktx2: "texture", basis: "texture",
    mp4: "video", webm: "video", mov: "video",
    mp3: "audio", wav: "audio", ogg: "audio", opus: "audio", flac: "audio",
    ts: "code", js: "code", mjs: "code", glsl: "code", wgsl: "code",
    json: "text", md: "text", txt: "text", csv: "text", xml: "text", yaml: "text", yml: "text",
    pdf: "document",
    zip: "archive", "7z": "archive", rar: "archive", tar: "archive", gz: "archive", tgz: "archive",
};

/**
 * Extension-inferred SEMANTIC type, or null when the path carries no KNOWN
 * extension (extension-less cloud srcs like ".../file") — callers with more
 * context (content-type header, the use itself) decide the fallback.
 */
export function inferAssetTypeFromExtension(path: string): AssetType | null {
    const name = path.split("/").pop() ?? path;
    const dot = name.lastIndexOf(".");
    const ext = dot >= 0 ? name.substring(dot + 1).toLowerCase() : "";
    return EXTENSION_TYPES[ext] ?? null;
}

/**
 * Needle Cloud `content_type` → our semantic asset type. The cloud's
 * classification is AUTHORITATIVE for cloud assets — a material glb is
 * indistinguishable from a model glb by extension, so consumers use this
 * first and fall back to extension inference only when it is absent
 * (older servers).
 */
export function assetTypeFromCloudContentType(contentType: string | null | undefined): AssetType | null {
    switch (contentType) {
        case "material": return "material";
        case "hdri": return "hdr";
        case "3d-asset": return "model";
        default: return null;
    }
}

/**
 * Extension-inferred SEMANTIC type — the browse-time default for files nothing
 * references yet, and the writer's fallback when minting a document `kind`. The
 * real kind is decided at USE (a png used on a material slot is a texture, used
 * in UI an image); use-sites override this. Plain raster images default to
 * `image`, not `texture`.
 */
export function inferAssetType(path: string): AssetType {
    return inferAssetTypeFromExtension(path) ?? "text";
}

/**
 * Semantic type from a Content-Type header — the fallback rung when a src has no
 * usable extension (the writer HEAD-requests the url and asks the server).
 * Null for absent/opaque types (application/octet-stream) — the caller's
 * use-based default takes over.
 */
export function assetTypeFromMimeType(mime: string | null | undefined): AssetType | null {
    if (!mime) return null;
    const type = mime.split(";")[0].trim().toLowerCase();
    if (type === "model/gltf-binary" || type === "model/gltf+json") return "model";
    if (type.startsWith("model/")) return "model";
    if (type === "image/vnd.radiance" || type === "image/x-exr" || type === "image/aces") return "hdr";
    if (type === "image/ktx2" || type === "image/basis") return "texture";
    if (type.startsWith("image/")) return "image";
    if (type.startsWith("video/")) return "video";
    if (type.startsWith("audio/")) return "audio";
    if (type === "text/javascript" || type === "application/javascript" || type === "application/typescript") return "code";
    if (type === "application/json" || type.startsWith("text/")) return "text";
    if (type === "application/pdf") return "document";
    if (type === "application/zip" || type === "application/x-7z-compressed" || type === "application/vnd.rar"
        || type === "application/x-tar" || type === "application/gzip") return "archive";
    return null;
}

const EXTENSION_MIMETYPES: Record<string, string> = {
    glb: "model/gltf-binary", gltf: "model/gltf+json", fbx: "model/fbx", obj: "model/obj", usdz: "model/vnd.usdz+zip", vrm: "model/gltf-binary",
    hdr: "image/vnd.radiance", exr: "image/x-exr",
    png: "image/png", jpg: "image/jpeg", jpeg: "image/jpeg", webp: "image/webp", avif: "image/avif", gif: "image/gif", svg: "image/svg+xml",
    ktx2: "image/ktx2", basis: "image/basis",
    mp4: "video/mp4", webm: "video/webm", mov: "video/quicktime",
    mp3: "audio/mpeg", wav: "audio/wav", ogg: "audio/ogg", opus: "audio/opus", flac: "audio/flac",
    ts: "application/typescript", js: "text/javascript", mjs: "text/javascript", glsl: "text/plain", wgsl: "text/plain",
    json: "application/json", md: "text/markdown", txt: "text/plain", csv: "text/csv", xml: "application/xml", yaml: "application/yaml", yml: "application/yaml",
    pdf: "application/pdf",
    zip: "application/zip", "7z": "application/x-7z-compressed", rar: "application/vnd.rar", tar: "application/x-tar", gz: "application/gzip", tgz: "application/gzip",
};

/** Extension-derived mime type — display metadata for local files nothing has
 * served yet (the inspector's mime row). Null for unknown extensions — never
 * a guess; a served asset's Content-Type header wins where one exists. */
export function mimeTypeFromPath(path: string): string | null {
    const name = path.split("/").pop() ?? path;
    const dot = name.lastIndexOf(".");
    const ext = dot >= 0 ? name.substring(dot + 1).toLowerCase() : "";
    return EXTENSION_MIMETYPES[ext] ?? null;
}

/**
 * Per-type interaction hint for asset tooltips — the click/double-click/drag UX
 * is uniform across all sources, so the wording lives in one place. Empty when
 * a type has no apply gesture (click only selects).
 */
export function assetActionHint(type: AssetType | ({} & string)): string | undefined {
    switch (type) {
        case "hdr": return "Double-click to use as scene environment";
        case "material": return "Double-click to apply to the selected object, or drag onto a mesh";
        case "model": return "Double-click or drag into the scene to add";
        case "texture": case "image": return "Drag onto a material slot to assign";
    }
    return undefined;
}

/**
 * The tooltip for an asset — the SAME text wherever you hover it.
 *
 * The thumbnail and the name used to build their own: the name showed tags,
 * category and a link while the thumbnail showed only the name and the gesture.
 * Same asset, same question, two answers depending on which pixel you were over.
 *
 * A field with nothing to say is dropped rather than rendered empty — the older
 * text printed `Tags: undefined` (joining a missing array) and a dangling
 * `Link: (new tab)` pointing at nothing.
 *
 * Markdown: the tooltip renderer draws it, and the link has to be clickable.
 */
export function assetTooltip(asset: Asset): string {
    const lines: string[] = [];
    // description over name: the name is already under the thumbnail, so it is
    // the description that cannot be read any other way
    const heading = asset.description?.trim() || asset.name?.trim();
    if (heading) lines.push(heading);

    const facts: string[] = [];
    const tags = asset.tags?.filter(Boolean).join(", ");
    if (tags) facts.push(`**Tags**: ${tags}`);
    if (asset.category) facts.push(`**Category**: ${asset.category}`);
    if (asset.externalLink) {
        facts.push(`**Link**: [${asset.name || "Open"}](${asset.externalLink}) (new tab)`);
    }
    if (facts.length) lines.push(facts.join("\n"));

    const hint = assetActionHint(asset.type);
    if (hint) lines.push(hint);

    return lines.join("\n\n");
}

export function assetTypeDisplayName(type: AssetType | ({} & string)): string {
    switch (type) {
        case "texture": return "Texture";
        case "model": return "3D Model";
        case "hdr": return "HDRI";
        case "material": return "Material";
        case "image": return "Image";
        case "video": return "Video";
        case "audio": return "Audio";
        case "code": return "Code";
        case "text": return "Text";
        case "document": return "Document";
        case "archive": return "Archive";
    }
    return type;
}

export interface Asset {
    readonly type: AssetType;
    /** identity WITHIN the provider. Project assets: the folder-relative path while
     * unreferenced (nothing points at them — the path is enough); a referencing
     * document mints a stable guid in its asset entry (document-scoped identity). */
    readonly id: string;
    readonly provider?: string;
    /** current location for library-backed assets (project: folder-relative path).
     * A LOCATION, not identity — assets may move. */
    path?: string;
    /** content hash (sha-256, hex) — move-healing, dedupe and integrity, NEVER
     * identity (editing an asset changes its content but not what references mean) */
    contentHash?: string;
    /** what the owning library allows for THIS entry — the UI renders affordances
     * from it (remote entries: no delete; generated project files: protected) */
    capabilities?: { delete?: boolean };
    mime?: string;
    category?: string;
    tags?: string[];
    name?: string;
    description?: string;
    thumbnailUrl?: string;
    thumbnailResolution?: { width: number; height: number };
    createdAt?: number;
    updatedAt?: number;
    externalLink?: string;
    /** file size in bytes, when the library knows it cheaply (project files) */
    fileSize?: number;
}

/** The src an ASSIGN OP (and through it the saved document) carries for an
 * asset — the PORTABLE identity, never a session-local loadable uri: a
 * project asset's folder-relative path resolves against the document like
 * every relative src in the format (a `tmp:/…` uri is dead on reload, dead
 * on peers, and invisible to the cloud push). Remote assets have no `path`;
 * their served url IS the portable src. */
export function portableAssetSrc(asset: Pick<Asset, "path">, loadableUri: string): string {
    return asset.path ?? loadableUri;
}

/** Selector-value convention for browsing a SOURCE from a type dropdown
 *  ("source:project") — keeps the two axes distinguishable in one control. */
export const ASSET_SOURCE_VALUE_PREFIX = "source:";
/** the dropdown value for a source id */
export function assetSourceValue(sourceId: string): string {
    return ASSET_SOURCE_VALUE_PREFIX + sourceId;
}
/** parse a dropdown value back into fetch options (type XOR source) */
export function assetSelectionToFetchOptions(value: string): AssetFetchOptions {
    return value.startsWith(ASSET_SOURCE_VALUE_PREFIX)
        ? { source: value.substring(ASSET_SOURCE_VALUE_PREFIX.length) }
        : { type: value as AssetType };
}

/** a browsable asset SOURCE (a provider that appears as its own library in the UI,
 *  e.g. the project folder) — orthogonal to the semantic `types` axis */
export interface AssetSource {
    readonly id: string;
    readonly label: string;
    readonly capabilities?: { import?: boolean; delete?: boolean };
}

export interface AssetOptionsResult {
    types: AssetType[];
    categories?: { [type in AssetType]?: string[] };
    tags?: { [type in AssetType]?: string[] };
    /** browsable sources ("Project"); absent = type-routed remote browsing only */
    sources?: AssetSource[];
}

/**
 * `type` and `source` are ORTHOGONAL axes: no source = the default per-type provider
 * routing (remote libraries); a source routes to that provider's library, with `type`
 * as an optional filter within it.
 */
export interface AssetFetchOptions {
    type?: AssetType;
    source?: string;
    category?: string[];
    tags?: string[];
}

export interface AssetImportRequest {
    /** target source ({@link AssetSource.id}) — v0: "project" */
    readonly source: string;
    /** file name (used as-is; collisions get a free-name suffix) */
    readonly name: string;
    readonly data: VSBuffer;
    /** semantic kind when the import context knows it (drop on a material slot →
     * texture); otherwise inferred from the extension at list time */
    readonly type?: AssetType;
}

export interface AssetFetchResult {
    assets: Asset[];
}

export interface AssetFetchProgress {

}



export interface FullAsset {
    readonly id: string;
    readonly versions: AssetVersion[];
    /** resolved display name (provider detail — cloud: the content title) */
    readonly name?: string;
    /** the provider's page for the asset (cloud: the EDIT page) */
    readonly externalLink?: string;
}

/** Identifies an asset (+ which version) so its resource resolver can be recovered later. */
export interface AssetResourceRef {
    /** provider asset id ({@link Asset.id}) */
    readonly id: string;
    /** provider key ({@link Asset.provider}) — routes to the owning provider */
    readonly provider?: string;
    /** the `src` recorded in the document; picks the matching version when a provider has several */
    readonly src?: string;
}

/** How an asset's external resources resolve — the version's fetch base + url rewrite. */
export interface AssetResourceResolution {
    /** the resolved fetch uri for the asset itself (may differ from the requested `src`) */
    readonly src?: URI;
    readonly resourcePath?: string;
    readonly resolveUrl?: (url: string) => string;
}
export interface AssetVersion {
    /**
     * URI to fetch the asset (e.g. download link or file path)
     */
    uri?: URI;

    /**
     * Raw data of the asset version (if already loaded)
     */
    data?: VSBuffer;

    /**
     * Version string, e.g. "1.0.0"
     */
    version?: string;
    /**
     * Optional content MD5 hash for integrity check
     */
    contentMd5?: string;
    /**
     * File size in bytes
     */
    fileSize?: number;
    /**
     * Additional metadata depending on asset type
     */
    resolution?: "1k" | "2k" | "4k" | "8k" | "16k" | number | { width: number; height: number };
    /**
     * Tags associated with this version
     */
    tags?: string[];
    /**
     * File format, e.g. "jpg", "png", "hdr", "exr"
     */
    format?: "gltf" | "jpg" | "png" | "hdr" | "exr" | ({} & string);

    resourcePath?: string;

    resolveUrl?: (url: string) => string;

    /**
     * Total size of all related files (e.g. for glTF with external resources)
     */
    totalSize?: number;

    /**
     * The version streams LODs progressively (gltf-progressive). Loading it
     * needs a runtime with progressive support — a plain GLTFLoader shows the
     * lowres base mesh forever. The default version pick avoids these on
     * platforms without that runtime (the chrome extension inspecting vanilla
     * three.js pages).
     */
    progressive?: boolean;
}

// #region AssetOpener

export type AssetInteraction = {
    type: ({} & string);
    assets: Asset[];
    targets?: unknown[];
    noTelemetry?: boolean;
    position?: any;
}  & (
        {
            /** Dragdrop */
            type: "dragdrop";
            position: { x: number; y: number };
        } |
        {
            /** Use selection */
            type: "selected";
        } | 
        {
            type: "targets";
            /** Target objects, can be a property or a 3D object in the scene that should receive e.g. the material */
            targets: Array<IProperty | object>;
        }
    )

export function isDragDropInteraction(interaction: AssetInteraction): boolean {
    return interaction.type === "dragdrop";
}

/**
 * Checks if the given interaction is a "select" interaction, e.g. when clicking an asset in the asset browser
 */
export function isSelectionInteraction(interaction: AssetInteraction): boolean {
    return interaction.type === "selected";
}

export function isTargetsInteraction(interaction: AssetInteraction): boolean {
    return interaction.type === "targets";
}

export interface IAssetInteractionHandler {
    canOpenAsset(accessor: ServicesAccessor, interaction: AssetInteraction): boolean;
    openAsset(accessor: ServicesAccessor, interaction: AssetInteraction, token?: CancellationToken): Promise<boolean>;
}